import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { billingApi, type BillingProfile } from '@/api/billing';
import { ErrorCodes, isApiError } from '@/api/errors';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { SubmitButton } from '@/components/SubmitButton';

export function BillingIntervalControl({
  billing,
  onRefresh,
  ending = false,
}: {
  billing: BillingProfile;
  onRefresh: () => Promise<void>;
  ending?: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [annualUnavailable, setAnnualUnavailable] = useState(false);
  const inFlight = useRef(false);
  const subscription = billing.subscription;
  if (!subscription?.interval) return null;
  const target = subscription.interval === 'MONTH' ? 'YEAR' : 'MONTH';
  const date = subscription.renewsOn ?? subscription.currentPeriodEnd?.slice(0, 10);
  const formattedDate = date ? formatCivilDate(date, locale) : '—';
  const amount =
    target === 'YEAR' ? billing.prices?.annual?.amountMinor : (billing.prices?.baseMinor ?? null);
  const price =
    amount != null && billing.prices?.currency
      ? formatMoneyMinor(amount, billing.prices.currency, locale)
      : null;
  const canSwitch =
    subscription.status === 'ACTIVE' &&
    subscription.origin === 'SELF' &&
    !ending &&
    !subscription.pendingChange &&
    (target === 'MONTH' || (!!billing.prices?.annual && !annualUnavailable));

  async function submit(cancel: boolean) {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      if (cancel) await billingApi.cancelIntervalChange();
      else await billingApi.changeInterval(target);
      await onRefresh();
      setOpen(false);
    } catch (cause) {
      const code = isApiError(cause) ? cause.code : null;
      if (isApiError(cause) && code === ErrorCodes.PLAN_MAPPING_CONFLICT && import.meta.env.DEV) {
        console.warn('interval: plan mapping conflict', cause.code, cause.requestId);
      }
      setError(
        code === ErrorCodes.INTERVAL_NOT_OFFERED
          ? 'intervalBilling.notOffered'
          : code === ErrorCodes.PAY_API_UNAVAILABLE
            ? 'subscribe.errorPayApi'
            : code === ErrorCodes.PLAN_MAPPING_CONFLICT
              ? 'intervalBilling.errorPlanMapping'
              : 'settings.errorGeneric',
      );
      if (code === ErrorCodes.INTERVAL_NOT_OFFERED) {
        setAnnualUnavailable(true);
        try {
          await onRefresh();
        } catch {
          /* Preserve the actionable mutation error. */
        }
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <Stack spacing={2}>
      <Typography>{t(`intervalBilling.term.${subscription.interval}`)}</Typography>
      {date && <Typography>{t('intervalBilling.renews', { date: formattedDate })}</Typography>}
      {error && !open && <Alert severity="error">{t(error)}</Alert>}
      {subscription.pendingChange && (
        <Alert
          severity="info"
          role="status"
          aria-live="polite"
          action={
            subscription.origin === 'SELF' && (
              <Button color="inherit" loading={busy} onClick={() => void submit(true)}>
                {t(`intervalBilling.keep.${subscription.interval}`)}
              </Button>
            )
          }
        >
          {t(`intervalBilling.pending.${subscription.pendingChange.interval}`, {
            date: formatCivilDate(subscription.pendingChange.effectiveOn, locale),
          })}
        </Alert>
      )}
      {canSwitch && (
        <Button
          variant="outlined"
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => {
            setError(null);
            setOpen(true);
          }}
        >
          {t(`intervalBilling.action.${target}`)}
        </Button>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="interval-dialog-title"
      >
        <DialogTitle id="interval-dialog-title">
          {t(`intervalBilling.action.${target}`)}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(`intervalBilling.confirm.${target}${price === null ? 'NoPrice' : ''}`, {
              date: formattedDate,
              price,
            })}
          </DialogContentText>
          {error && <Alert severity="error">{t(error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <SubmitButton
            loading={busy}
            disabled={target === 'YEAR' && annualUnavailable}
            onClick={() => void submit(false)}
          >
            {t(`intervalBilling.switch.${target}`)}
          </SubmitButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
